import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { MessageSquare, MessageCircle, Facebook, Mail, Copy, CheckCircle2, AlertTriangle, Play, Activity, RefreshCw, Link2, Unlink, Music2, Target, Eye, EyeOff, Megaphone, Send } from 'lucide-react';
import api from '../services/api';
import MetaConversionsSection from '../components/MetaConversionsSection';
import { loadFacebookSdk } from '../utils/facebookSdk';

const Section = ({ title, Icon, status, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={20} className="text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {status === 'ok' && <span className="text-xs inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-full"><CheckCircle2 size={12} /> Configured</span>}
      {status === 'warn' && <span className="text-xs inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-full"><AlertTriangle size={12} /> Setup required</span>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="mb-3">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const CopyableUrl = ({ url }) => (
  <div className="flex items-center gap-2">
    <code className="flex-1 text-xs font-mono bg-gray-50 p-2 border border-gray-200 rounded break-all">{url}</code>
    <button
      type="button"
      onClick={() => { navigator.clipboard?.writeText(url); toast.success('Copied'); }}
      className="p-2 text-gray-500 hover:text-primary-600"
    >
      <Copy size={14} />
    </button>
  </div>
);

const outcomePill = (o) => {
  const map = {
    ok:           'bg-green-100 text-green-800',
    skipped:      'bg-amber-100 text-amber-800',
    error:        'bg-red-100 text-red-800',
    unauthorized: 'bg-red-100 text-red-800',
  };
  return map[o] || 'bg-gray-100 text-gray-700';
};

const channelLabel = (c) => ({
  'meta-verify':  'Meta · verify',
  'meta-submit':  'Meta · submit',
  'google-ads':   'Google Ads',
  'whatsapp-verify': 'WhatsApp · verify',
  'whatsapp-submit': 'WhatsApp · submit',
}[c] || c);


const InboundSettings = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(
    ['inbound-settings'],
    () => api.get('/inbound-settings').then((r) => r.data)
  );

  const { data: events, refetch: refetchEvents, isFetching: fetchingEvents } = useQuery(
    ['inbound-recent-events'],
    () => api.get('/inbound-settings/recent-events').then((r) => r.data),
    { refetchInterval: 10_000 }
  );

  const disconnectWhatsapp = useMutation(
    () => api.delete('/inbound-settings/whatsapp'),
    {
      onSuccess: () => {
        toast.success('WhatsApp disconnected');
        queryClient.invalidateQueries(['inbound-settings']);
        queryClient.invalidateQueries(['whatsapp-status']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to disconnect'),
    }
  );

  // ── WhatsApp Embedded Signup ("Connect with WhatsApp") ──────────────
  // Meta's popup talks to us two ways: FB.login()'s callback returns a
  // one-time `code`, and the popup postMessages the chosen waba_id /
  // phone_number_id (type WA_EMBEDDED_SIGNUP). We stash the latter in a ref
  // and send both to the backend, which exchanges the code server-side.
  const [waSignupBusy, setWaSignupBusy] = useState(false);
  const waSessionRef = useRef(null);

  const waSdkReady = data?.whatsapp?.embeddedSignupReady;
  const waAppId = data?.whatsapp?.appId;
  const waGraphVersion = data?.whatsapp?.graphVersion;
  useEffect(() => {
    if (waSdkReady && waAppId) {
      loadFacebookSdk(waAppId, waGraphVersion || 'v23.0').catch(() => { /* surfaced on click */ });
    }
  }, [waSdkReady, waAppId, waGraphVersion]);

  useEffect(() => {
    const handler = (event) => {
      if (!/^https:\/\/([a-z0-9-]+\.)*facebook\.com$/.test(event.origin)) return;
      let msg;
      try { msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch (_) { return; }
      if (!msg || msg.type !== 'WA_EMBEDDED_SIGNUP') return;
      const finished = ['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'];
      if (finished.includes(msg.event)) {
        waSessionRef.current = {
          wabaId: msg.data?.waba_id ? String(msg.data.waba_id) : '',
          phoneNumberId: msg.data?.phone_number_id ? String(msg.data.phone_number_id) : '',
        };
      } else if (msg.event === 'CANCEL') {
        waSessionRef.current = { cancelled: true };
      } else if (msg.event === 'ERROR') {
        toast.error(msg.data?.error_message || 'WhatsApp signup failed inside the Meta popup');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const finishWhatsappSignup = useMutation(
    (payload) => api.post('/inbound-settings/whatsapp/embedded-signup', payload).then((r) => r.data),
    {
      onSuccess: (res) => {
        if (res.needsPhoneSelection) {
          toast('Pick which number to connect below', { icon: '📱' });
        } else if (res.registrationError) {
          toast.success(`WhatsApp connected: ${res.displayPhone}`);
          toast.error(`Number registration needs attention: ${res.registrationError}`, { duration: 9000 });
        } else {
          toast.success(`WhatsApp connected: ${res.displayPhone}`);
        }
        queryClient.invalidateQueries(['inbound-settings']);
        queryClient.invalidateQueries(['whatsapp-status']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not complete WhatsApp signup'),
      onSettled: () => setWaSignupBusy(false),
    }
  );

  const selectWhatsappPhone = useMutation(
    (phoneNumberId) => api.post('/inbound-settings/whatsapp/select-phone', { phoneNumberId }).then((r) => r.data),
    {
      onSuccess: (res) => {
        toast.success(`WhatsApp connected: ${res.displayPhone}`);
        if (res.registrationError) toast.error(`Number registration needs attention: ${res.registrationError}`, { duration: 9000 });
        queryClient.invalidateQueries(['inbound-settings']);
        queryClient.invalidateQueries(['whatsapp-status']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not connect the selected number'),
    }
  );

  // mode: 'coexistence' keeps the number active on the WhatsApp Business
  // phone app (Meta's whatsapp_business_app_onboarding flow — the customer
  // scans a QR from the app); 'cloud' is the classic flow for a number
  // that will live on the Cloud API only.
  const startWhatsappSignup = (mode) => {
    const wa = data?.whatsapp;
    if (!wa?.embeddedSignupReady) {
      toast.error('WhatsApp Embedded Signup is not configured on the server yet');
      return;
    }
    const run = (FB) => {
      waSessionRef.current = null;
      setWaSignupBusy(true);
      FB.login(
        (response) => {
          const code = response?.authResponse?.code;
          if (!code) {
            setWaSignupBusy(false);
            if (!waSessionRef.current?.cancelled) toast.error('WhatsApp signup was not completed');
            return;
          }
          // The popup's postMessage normally lands before this callback;
          // give a slow one a beat before we send what we have.
          setTimeout(() => {
            const sess = waSessionRef.current && !waSessionRef.current.cancelled ? waSessionRef.current : {};
            finishWhatsappSignup.mutate({ code, wabaId: sess.wabaId || '', phoneNumberId: sess.phoneNumberId || '' });
          }, 400);
        },
        {
          config_id: wa.waConfigId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            sessionInfoVersion: '3',
            ...(mode === 'coexistence' ? { featureType: 'whatsapp_business_app_onboarding' } : {}),
          },
        }
      );
    };
    if (window.FB) {
      run(window.FB); // synchronous → popup allowed
    } else {
      loadFacebookSdk(wa.appId, wa.graphVersion || 'v23.0').then(run).catch((e) => toast.error(e.message));
    }
  };

  // IMAP credentials live on SMTP Settings now; this page just renders
  // status + the manual "Poll now" trigger. We keep the imap state
  // referenced by the legacy PATCH path out of the picture entirely.

  // Hook intentionally empty — IMAP form moved to SMTP Settings.

  // mode 'page' = the fallback: only the Page, with the classic dialog.
  const startMetaOAuth = useMutation(
    (mode) => api.get('/meta/oauth/start', mode ? { params: { mode } } : undefined).then((r) => r.data),
    {
      onSuccess: (r) => {
        if (r.url) window.location.href = r.url;
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not start Facebook login'),
    }
  );

  const disconnectMeta = useMutation(
    () => api.post('/meta/oauth/disconnect').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Disconnected from Facebook');
        queryClient.invalidateQueries(['inbound-settings']);
        // The single connection also carries the Conversions API.
        queryClient.invalidateQueries(['meta-conversions']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Disconnect failed'),
    }
  );

  // The import is a background job — kickoff returns 202 immediately so
  // the request doesn't hit Cloudflare's ~100s edge timeout on big
  // accounts. Progress lives on inbound-settings, polled below.
  const [requireContact, setRequireContact] = useState(false);
  const importHistoricalLeads = useMutation(
    (opts = {}) => api.post('/meta/oauth/import-historical-leads', opts).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Historical lead import started. Progress will update as it runs.', { duration: 5000 });
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not start import'),
    }
  );

  // Re-pull answers for Lead Ad leads that arrived while the Page token
  // was broken (nameless "Inbound contact" rows). Synchronous — it only
  // touches the org's incomplete leads, capped server-side.
  const repairLeadgenLeads = useMutation(
    () => api.post('/meta/oauth/repair-leadgen-leads', {}).then((r) => r.data),
    {
      onSuccess: (d) => {
        if (!d?.scanned) {
          toast.success('No incomplete Lead Ad leads found.');
        } else {
          toast.success(`Checked ${d.scanned} lead${d.scanned === 1 ? '' : 's'}: ${d.repaired} repaired, ${d.merged || 0} merged into existing leads, ${d.unchanged} unchanged${d.errors ? `, ${d.errors} failed` : ''}.`, { duration: 7000 });
        }
        queryClient.invalidateQueries(['leads']);
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Repair failed'),
    }
  );

  // Backfill campaign / ad set / ad names onto Lead Ad leads captured
  // before names were stored. Bounded per click; the toast says how many
  // are left so the operator can click again.
  const syncAdAttribution = useMutation(
    () => api.post('/meta/oauth/sync-ad-attribution', {}).then((r) => r.data),
    {
      onSuccess: (d) => {
        if (d?.tokenInvalid) {
          toast.error('Meta rejected the Page token. Reconnect the Page, then run the sync again.', { duration: 7000 });
        } else if (d?.rateLimited) {
          toast.error(`Meta rate limit reached after ${d.updated + d.unchanged + d.expired} lead${d.updated + d.unchanged + d.expired === 1 ? '' : 's'}. Wait about an hour and click again — nothing was lost.`, { duration: 9000 });
        } else if (!d?.scanned) {
          toast.success('All Lead Ad leads already have their ad names.');
        } else {
          const parts = [`${d.updated} updated`, `${d.unchanged} unchanged`];
          if (d.expired) parts.push(`${d.expired} older than 90 days (Meta no longer has the form data)`);
          if (d.errors) parts.push(`${d.errors} failed${d.errorSamples?.[0]?.message ? ` — ${d.errorSamples[0].message}` : ''}`);
          toast.success(`Checked ${d.scanned} lead${d.scanned === 1 ? '' : 's'}: ${parts.join(', ')}${d.remaining ? ` · ${d.remaining} left — click again` : ''}.`, { duration: 10000 });
        }
        queryClient.invalidateQueries(['leads']);
        queryClient.invalidateQueries(['reports', 'ad-attribution']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Sync failed'),
    }
  );

  // TikTok parallel — same shape as Meta. Lives next to the Meta state
  // so the polling effect below can watch both with one condition.
  const [tiktokRequireContact, setTiktokRequireContact] = useState(false);
  const importTiktokHistorical = useMutation(
    (opts = {}) => api.post('/tiktok/oauth/import-historical-leads', opts).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('TikTok historical import started. Progress will update as it runs.', { duration: 5000 });
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not start TikTok import'),
    }
  );

  // While an import is running, poll inbound-settings every 4s so the
  // operator gets live progress. Hook re-arms only while at least one
  // job is running to avoid burning a request budget after completion.
  const importStatus = data?.meta?.lastImport;
  const messengerStatus = data?.meta?.lastMessengerImport;
  const tiktokImportStatus = data?.tiktok?.lastImport;
  const anyRunning =
    importStatus?.status === 'running' ||
    messengerStatus?.status === 'running' ||
    tiktokImportStatus?.status === 'running';
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => queryClient.invalidateQueries(['inbound-settings']), 4000);
    return () => clearInterval(t);
  }, [anyRunning, queryClient]);

  // Toast when each run finishes — separate ref keys per import type so
  // they don't shadow each other.
  const lastToastedLeadsRef = useRef(null);
  useEffect(() => {
    if (!importStatus || importStatus.status === 'running') return;
    const key = `${importStatus.status}:${importStatus.finishedAt}`;
    if (lastToastedLeadsRef.current === key) return;
    lastToastedLeadsRef.current = key;
    if (importStatus.status === 'completed') {
      toast.success(
        `Lead import done — ${importStatus.captured} new, ${importStatus.alreadyExisted} already existed${importStatus.skippedNoContact ? `, ${importStatus.skippedNoContact} skipped (no contact)` : ''}${importStatus.errors ? `, ${importStatus.errors} errors` : ''}.`,
        { duration: 6000 }
      );
    } else if (importStatus.status === 'error') {
      toast.error(`Lead import failed: ${importStatus.errorMessage || 'unknown error'}`, { duration: 8000 });
    }
  }, [importStatus]);

  const lastToastedMessengerRef = useRef(null);
  useEffect(() => {
    if (!messengerStatus || messengerStatus.status === 'running') return;
    const key = `${messengerStatus.status}:${messengerStatus.finishedAt}`;
    if (lastToastedMessengerRef.current === key) return;
    lastToastedMessengerRef.current = key;
    if (messengerStatus.status === 'completed') {
      toast.success(
        `Messenger import done — ${messengerStatus.messagesIngested} message(s) across ${messengerStatus.conversationCount} thread(s); ${messengerStatus.leadsCreated} new lead(s), ${messengerStatus.leadsMatched} matched${messengerStatus.leadsSkippedNoContact ? `, ${messengerStatus.leadsSkippedNoContact} skipped (no contact)` : ''}.`,
        { duration: 6000 }
      );
    } else if (messengerStatus.status === 'error') {
      toast.error(`Messenger import failed: ${messengerStatus.errorMessage || 'unknown error'}`, { duration: 8000 });
    }
  }, [messengerStatus]);

  // Mirror for TikTok — separate ref so it doesn't fight with the Meta toasts.
  const lastToastedTiktokRef = useRef(null);
  useEffect(() => {
    if (!tiktokImportStatus || tiktokImportStatus.status === 'running') return;
    const key = `${tiktokImportStatus.status}:${tiktokImportStatus.finishedAt}`;
    if (lastToastedTiktokRef.current === key) return;
    lastToastedTiktokRef.current = key;
    if (tiktokImportStatus.status === 'completed') {
      toast.success(
        `TikTok import done — ${tiktokImportStatus.captured} new, ${tiktokImportStatus.alreadyExisted} already existed${tiktokImportStatus.skippedNoContact ? `, ${tiktokImportStatus.skippedNoContact} skipped (no contact)` : ''}${tiktokImportStatus.errors ? `, ${tiktokImportStatus.errors} errors` : ''}.`,
        { duration: 6000 }
      );
    } else if (tiktokImportStatus.status === 'error') {
      toast.error(`TikTok import failed: ${tiktokImportStatus.errorMessage || 'unknown error'}`, { duration: 8000 });
    }
  }, [tiktokImportStatus]);

  const startTiktokOAuth = useMutation(
    () => api.get('/tiktok/oauth/start').then((r) => r.data),
    {
      onSuccess: (r) => {
        if (r.url) window.location.href = r.url;
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not start TikTok login'),
    }
  );

  const disconnectTiktok = useMutation(
    () => api.post('/tiktok/oauth/disconnect').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Disconnected from TikTok');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Disconnect failed'),
    }
  );

  const resubscribeTiktok = useMutation(
    () => api.post('/tiktok/oauth/resubscribe').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('TikTok webhook subscription registered. Lead events should start flowing.');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Re-subscribe failed'),
    }
  );


  // Google Ads — per-org webhook + shared `google_key`. No OAuth, just a
  // secret the operator pastes into Google's Lead Form Asset config.
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const regenerateGoogleSecret = useMutation(
    () => api.post('/inbound-settings/google/regenerate-secret').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Generated a fresh Google Ads webhook secret');
        setShowGoogleSecret(true);
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not generate secret'),
    }
  );
  const disconnectGoogle = useMutation(
    () => api.delete('/inbound-settings/google').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Disconnected from Google Ads');
        setShowGoogleSecret(false);
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Disconnect failed'),
    }
  );

  // V2 OAuth — "Connect with Google" path. Picker key + customer list
  // populated after Google redirects back with ?google_picker=KEY.
  const [googlePickerKey, setGooglePickerKey] = useState(null);
  const [googleCustomers, setGoogleCustomers] = useState([]);
  const [googleSelectedCustomer, setGoogleSelectedCustomer] = useState('');
  const [googleNeedDevToken, setGoogleNeedDevToken] = useState(false);
  const [googleNameLookupError, setGoogleNameLookupError] = useState(null);

  const startGoogleOAuth = useMutation(
    () => api.get('/google-ads/oauth/start').then((r) => r.data),
    {
      onSuccess: (r) => {
        if (r.url) window.location.href = r.url;
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not start Google login'),
    }
  );

  const selectGoogleCustomer = useMutation(
    (body) => api.post('/google-ads/oauth/select-customer', body).then((r) => r.data),
    {
      onSuccess: (r) => {
        const attach = r.attach || {};
        if (attach.skipped) {
          toast.success(`Connected to ${r.customerName}. Webhook auto-attach pending Google API approval.`);
        } else if (attach.total > 0) {
          toast.success(`Connected to ${r.customerName} — webhook attached to ${attach.succeeded}/${attach.total} lead form(s).`);
        } else {
          toast.success(`Connected to ${r.customerName}. No lead forms found yet — create one in Google Ads.`);
        }
        setGooglePickerKey(null);
        setGoogleCustomers([]);
        setGoogleSelectedCustomer('');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Select customer failed'),
    }
  );

  const disconnectGoogleOAuth = useMutation(
    () => api.post('/google-ads/oauth/disconnect').then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Disconnected Google Ads OAuth');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Disconnect failed'),
    }
  );

  const attachGoogleWebhooks = useMutation(
    () => api.post('/google-ads/oauth/attach-webhooks').then((r) => r.data),
    {
      onSuccess: (r) => {
        toast.success(`Webhook attached to ${r.succeeded}/${r.total} lead form(s)`);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Attach failed'),
    }
  );

  // Synthetic Google Ads Lead Form payload posted against our own webhook
  // for end-to-end verification. Useful for fresh accounts with no campaigns
  // — proves the URL + secret are wired correctly without touching Google.
  const sendGoogleTestLead = useMutation(
    () => api.post('/inbound-settings/google/send-test-lead').then((r) => r.data),
    {
      onSuccess: (r) => {
        toast.success(`Test lead created: ${r.leadEmail}. Check your Inbox.`);
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Test failed'),
    }
  );

  // Picker state — populated after Meta redirects back with ?meta_picker=KEY
  const [pickerKey, setPickerKey] = useState(null);
  // Last Facebook Login failure, kept so the Meta card can explain it.
  const [metaOAuthError, setMetaOAuthError] = useState('');
  const [pickerPages, setPickerPages] = useState([]);
  const [pickerNeedPageId, setPickerNeedPageId] = useState(false);
  const [pickerManualPageId, setPickerManualPageId] = useState('');
  const [pickerSelected, setPickerSelected] = useState('');

  // TikTok picker state — fully separate from Meta's. Populated after
  // the TikTok callback redirects back with ?tiktok_picker=KEY when the
  // user authorised more than one Ads Manager account on the consent
  // screen.
  const [tiktokPickerKey, setTiktokPickerKey] = useState(null);
  const [tiktokAdvertisers, setTiktokAdvertisers] = useState([]);
  const [tiktokSelected, setTiktokSelected] = useState('');

  // Read URL params once on mount: success / error / picker handoff.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('meta_connected');
    const oauthError = params.get('meta_oauth_error');
    const picker = params.get('meta_picker');
    const needPageId = params.get('need_page_id');
    if (connected) {
      toast.success(`Connected: ${connected}`);
      queryClient.invalidateQueries(['inbound-settings']);
    }
    if (oauthError) {
      toast.error(`Facebook: ${oauthError}`);
      setMetaOAuthError(oauthError);
    }
    if (picker) {
      setPickerKey(picker);
      setPickerNeedPageId(needPageId === '1');
      api.get(`/meta/oauth/pending/${picker}`)
        .then((r) => {
          setPickerPages(r.data.pages || []);
          setPickerNeedPageId(r.data.needPageId || needPageId === '1');
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || 'Picker session expired');
          setPickerKey(null);
        });
    }
    // Clean the URL so reload doesn't replay these.
    if (connected || oauthError || picker) {
      const url = new URL(window.location.href);
      ['meta_connected', 'meta_oauth_error', 'meta_picker', 'need_page_id'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.toString());
    }

    // Same shape for TikTok callback redirects
    const ttConnected = params.get('tiktok_connected');
    const ttError = params.get('tiktok_oauth_error');
    const ttPicker = params.get('tiktok_picker');
    if (ttConnected) {
      toast.success(`Connected to TikTok: ${ttConnected}`);
      queryClient.invalidateQueries(['inbound-settings']);
    }
    if (ttError) toast.error(`TikTok: ${ttError}`);
    if (ttPicker) {
      // Multiple Ads Manager accounts authorised — fetch the list so
      // the operator can pick which one this org should track. Mirrors
      // Meta's pattern but uses TikTok-specific endpoints + state so
      // the two flows can't interfere with each other.
      setTiktokPickerKey(ttPicker);
      api.get(`/tiktok/oauth/pending/${ttPicker}`)
        .then((r) => {
          setTiktokAdvertisers(r.data.advertisers || []);
          if ((r.data.advertisers || []).length === 1) {
            setTiktokSelected(r.data.advertisers[0].advertiserId);
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || 'TikTok picker session expired');
          setTiktokPickerKey(null);
        });
    }
    if (ttConnected || ttError || ttPicker) {
      const url = new URL(window.location.href);
      ['tiktok_connected', 'tiktok_oauth_error', 'tiktok_picker'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.toString());
    }

    // Google Ads OAuth callback handoff. Same picker pattern as Meta — the
    // backend stashes the customer list in a pending row and redirects us
    // here with the key. Fetch + render picker.
    const gError = params.get('google_oauth_error');
    const gPicker = params.get('google_picker');
    const gNeedDevToken = params.get('need_dev_token');
    if (gError) toast.error(`Google: ${gError}`);
    if (gPicker) {
      setGooglePickerKey(gPicker);
      setGoogleNeedDevToken(gNeedDevToken === '1');
      api.get(`/google-ads/oauth/pending/${gPicker}`)
        .then((r) => {
          setGoogleCustomers(r.data.customers || []);
          setGoogleNeedDevToken(r.data.needDevToken || gNeedDevToken === '1');
          setGoogleNameLookupError(r.data.nameLookupError || null);
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || 'Google picker session expired');
          setGooglePickerKey(null);
        });
    }
    if (gError || gPicker) {
      const url = new URL(window.location.href);
      ['google_oauth_error', 'google_picker', 'need_dev_token'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.toString());
    }
  }, [queryClient]);

  const submitPagePicker = useMutation(
    (body) => api.post('/meta/oauth/select-page', body).then((r) => r.data),
    {
      onSuccess: (r) => {
        toast.success(r.pageName ? `Connected: ${r.pageName}` : 'Connected');
        setPickerKey(null);
        setPickerPages([]);
        setPickerSelected('');
        setPickerManualPageId('');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Select page failed'),
    }
  );

  const submitTiktokPicker = useMutation(
    (body) => api.post('/tiktok/oauth/select-advertiser', body).then((r) => r.data),
    {
      onSuccess: (r) => {
        toast.success(r.advertiserName ? `Connected to TikTok: ${r.advertiserName}` : 'Connected to TikTok');
        setTiktokPickerKey(null);
        setTiktokAdvertisers([]);
        setTiktokSelected('');
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'TikTok advertiser selection failed'),
    }
  );

  // Legacy IMAP save mutation removed — IMAP host now lives on SMTP Settings.
  // Kept as a noop ref so any stray reference doesn't crash; safe to delete
  // in a future cleanup pass.
  const saveImap = useMutation(
    () => Promise.resolve(),
    {
      onSuccess: () => {},
      onError: () => {},
    }
  );

  const testImap = useMutation(
    () => api.post('/inbound-settings/imap/test').then((r) => r.data),
    {
      onSuccess: (r) => {
        // Surface every counter the poller reports so operators can tell
        // "auth ok but inbox empty" from "auth ok but no senders match
        // an existing lead". When the poll hit any errors, show the first
        // error message inline as a toast.error — operators can act on it
        // without opening DevTools.
        const total = r.total ?? 0;
        const cap = r.captured ?? 0;
        const nl = r.skippedNoLead ?? 0;
        const self = r.skippedSelf ?? 0;
        const dup = r.skippedDuplicate ?? 0;
        const errs = r.errors || [];
        const parts = [`fetched ${total}`, `captured ${cap}`];
        if (nl) parts.push(`skipped ${nl} (no matching lead)`);
        if (self) parts.push(`skipped ${self} (sent by you)`);
        if (dup) parts.push(`skipped ${dup} (already captured)`);

        if (errs.length) {
          // Pick the first error verbatim — usually auth / host / TLS.
          toast.error(`IMAP error: ${errs[0]}`, { duration: 10000 });
          parts.push(`${errs.length} error(s)`);
          console.error('[IMAP] errors:', errs);
        } else {
          toast.success(parts.join(' · '), { duration: 6000 });
        }

        if (r.skippedSenders?.length) {
        }
        queryClient.invalidateQueries(['inbound-settings']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Test failed'),
    }
  );

  if (isLoading || !data) return <div className="p-8 text-gray-400">Loading...</div>;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Inbound Channels</h1>
        <p className="text-sm text-gray-600 mt-1">
          Let SMS replies, Meta Lead Ads submissions and Gmail/IMAP emails land on the right lead as inbound notes automatically.
        </p>
        {data.webhookSlug && (
          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-900 flex items-start gap-2">
            <span className="font-semibold whitespace-nowrap">Your webhook ID:</span>
            <code className="font-mono break-all">{data.webhookSlug}</code>
            <span className="ml-auto text-indigo-700/70">Each tenant gets its own — never share with other orgs.</span>
          </div>
        )}
      </div>

      {/* SMS — VoIP.ms-powered, no webhook to configure (poller-based). */}
      <Section title="SMS (Gain Rhino VoIP)" Icon={MessageSquare} status={data.sms.isConfigured ? 'ok' : 'warn'}>
        {!data.sms.isConfigured ? (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            Gain Rhino VoIP isn't configured yet. Open <a href="/voip-settings" className="underline">VoIP Settings</a>, pick Gain Rhino VoIP from the provider dropdown, and save your API credentials. Inbound SMS will start flowing automatically — no webhook setup required.
          </div>
        ) : (
          <div className="text-xs text-gray-600">
            Inbound SMS is polled from Gain Rhino VoIP every 30 seconds and attached to the matching lead's timeline. No webhook to configure on this page.
          </div>
        )}
      </Section>

      {/* Meta Lead Ads */}
      <Section title="Meta Lead Ads" Icon={Facebook} status={data.meta.isConfigured ? 'ok' : 'warn'}>
        {data.meta.appConfigReady === false && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            The platform's Meta App credentials aren't configured on the server yet. Your administrator needs to set <code>META_APP_ID</code>, <code>META_APP_SECRET</code> and <code>META_VERIFY_TOKEN</code> before you can connect a Facebook Page.
          </div>
        )}

        {/* The single connection fails as a whole when Meta can't grant the
            Page to the business integration, typically because the person
            connecting has only partial access to the Page. Facebook's own
            redirect can't tell that apart from Cancel, so explain both. */}
        {data.meta.unifiedConnect && metaOAuthError && !pickerKey && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <div className="font-semibold">Facebook didn't finish the connection</div>
            <p className="mt-1">
              If Facebook said your Page "was not granted the requested permissions", your Facebook profile has only partial access to that Page.
              Ask the person with full control of the Page to give you full control (Business Settings → Pages → your Page → People) and connect again.
            </p>
            <p className="mt-1">
              {data.meta.isConfigured
                ? 'Your Page stays connected. To send lead stages to Meta, connect the ad account and dataset separately in the Meta Conversions API card below.'
                : 'Or connect the Page on its own now, and the ad account and dataset separately in the Meta Conversions API card below.'}
            </p>
            {!data.meta.isConfigured && (
              <button
                onClick={() => startMetaOAuth.mutate('page')}
                disabled={startMetaOAuth.isLoading}
                className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-amber-100 border border-amber-300 rounded-md font-medium disabled:opacity-50"
              >
                <Facebook size={12} /> Connect the Page only
              </button>
            )}
          </div>
        )}

        {!data.meta.isConfigured && !pickerKey && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {data.meta.unifiedConnect
                ? "One click connects everything Meta: your Facebook Page (Lead Ads, Messenger, Instagram DMs) and your ad account and dataset (Pixel) for the Meta Conversions API below. In the Facebook window, select your Page, Instagram account, ad account and dataset."
                : "One click to connect your Facebook Page. We'll ask Facebook for permission to receive Lead Ads on your behalf, then subscribe your page automatically — no tokens to copy, no developer console."}
            </p>
            <button
              onClick={() => startMetaOAuth.mutate()}
              disabled={startMetaOAuth.isLoading || data.meta.appConfigReady === false}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1877F2] hover:bg-[#166FE0] disabled:opacity-50 text-white rounded-md text-sm font-semibold shadow-sm"
            >
              <Facebook size={16} />
              {startMetaOAuth.isLoading ? 'Redirecting…' : 'Connect with Facebook'}
            </button>
            {data.meta.unifiedConnect && !metaOAuthError && (
              <p className="text-xs text-gray-500">
                Facebook shows an error about your Page?{' '}
                <button
                  onClick={() => startMetaOAuth.mutate('page')}
                  disabled={startMetaOAuth.isLoading}
                  className="font-medium text-[#1877F2] hover:underline disabled:opacity-50"
                >
                  Connect the Page only
                </button>
                {' '}and the dataset in the Meta Conversions API card below.
              </p>
            )}
          </div>
        )}

        {data.meta.isConfigured && !pickerKey && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Link2 size={16} className="text-green-700 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-green-900">Connected to Facebook</div>
                  <div className="text-green-800 mt-0.5">Page ID: <code className="font-mono text-xs">{data.meta.pageId}</code></div>
                  <div className="text-green-700 text-xs mt-1">Lead Ads from this page will land in your Inbox automatically.</div>
                  {data.meta.unifiedConnect && (
                    <div className="text-green-700 text-xs mt-1">
                      The same connection sends lead stages to Meta (Meta Conversions API below). Reconnect to add or change the ad account and dataset.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 shrink-0">
                {data.meta.unifiedConnect && (
                  <button
                    onClick={() => startMetaOAuth.mutate()}
                    disabled={startMetaOAuth.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-blue-50 border border-blue-200 text-[#1877F2] rounded-md text-xs font-medium"
                  >
                    <Facebook size={12} />
                    {startMetaOAuth.isLoading ? 'Redirecting…' : 'Reconnect'}
                  </button>
                )}
                <button
                  onClick={() => disconnectMeta.mutate()}
                  disabled={disconnectMeta.isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-700 rounded-md text-xs font-medium"
                >
                  <Unlink size={12} />
                  {disconnectMeta.isLoading ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
            {/* Historical backfill — single button kicks off two parallel
                workers (Lead Ads forms + Messenger / IG DM threads).
                Both update independent progress strips below. */}
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm flex-1">
                  <div className="font-semibold text-indigo-900">Import past Meta history</div>
                  <div className="text-indigo-800 text-xs mt-0.5">
                    Pulls every Lead Ad form submission and every Messenger / Instagram DM thread Meta has on file for this Page into the CRM in one shot. Already-imported records are skipped — safe to re-run.
                  </div>
                </div>
                <button
                  onClick={() => importHistoricalLeads.mutate({ requireContact })}
                  disabled={importHistoricalLeads.isLoading || anyRunning}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-xs font-medium whitespace-nowrap"
                >
                  {anyRunning ? 'Running…'
                    : importHistoricalLeads.isLoading ? 'Starting…'
                    : 'Import now'}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-indigo-900">
                <span>
                  Leads showing as "Inbound contact" with no email or phone? That happens when the Page token expired mid-stream.
                </span>
                <button
                  onClick={() => repairLeadgenLeads.mutate()}
                  disabled={repairLeadgenLeads.isLoading || anyRunning}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-md text-[11px] font-medium whitespace-nowrap disabled:opacity-50"
                >
                  <RefreshCw size={11} className={repairLeadgenLeads.isLoading ? 'animate-spin' : ''} />
                  {repairLeadgenLeads.isLoading ? 'Repairing…' : 'Repair incomplete leads'}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-indigo-900">
                <span>
                  Want to see which campaign and ad each lead came from? Pull the names from Meta for older Lead Ad leads.
                  New leads get them automatically.
                </span>
                <button
                  onClick={() => syncAdAttribution.mutate()}
                  disabled={syncAdAttribution.isLoading || anyRunning}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-indigo-100 border border-indigo-300 text-indigo-800 rounded-md text-[11px] font-medium whitespace-nowrap disabled:opacity-50"
                >
                  <Megaphone size={11} className={syncAdAttribution.isLoading ? 'animate-pulse' : ''} />
                  {syncAdAttribution.isLoading ? 'Syncing…' : 'Sync ad names'}
                </button>
              </div>
              <label className="mt-2 flex items-center gap-2 text-[11px] text-indigo-900">
                <input
                  type="checkbox"
                  checked={requireContact}
                  onChange={(e) => setRequireContact(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                />
                Skip only when both phone and email are missing
              </label>
              {importStatus && (
                <div className="mt-3 pt-3 border-t border-indigo-200 text-xs text-indigo-900">
                  {importStatus.status === 'running' && (
                    <div className="flex items-center gap-2">
                      <RefreshCw size={12} className="animate-spin" />
                      <span>
                        Running… {importStatus.captured} captured, {importStatus.alreadyExisted} already existed{importStatus.errors ? `, ${importStatus.errors} errors` : ''}
                        {importStatus.formCount ? ` (across ${importStatus.formCount} form${importStatus.formCount === 1 ? '' : 's'})` : ''}.
                      </span>
                    </div>
                  )}
                  {importStatus.status === 'completed' && (
                    <div>
                      <div>
                        Last run: <strong>{importStatus.captured}</strong> new, {importStatus.alreadyExisted} already existed{importStatus.skippedNoContact ? `, ${importStatus.skippedNoContact} skipped (no contact)` : ''}{importStatus.errors ? `, ${importStatus.errors} errors` : ''} — finished {importStatus.finishedAt ? new Date(importStatus.finishedAt).toLocaleString() : ''}.
                      </div>
                      {importStatus.errors > 0 && Array.isArray(importStatus.errorSamples) && importStatus.errorSamples.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-red-700 hover:text-red-900 select-none">
                            View {importStatus.errorSamples.length === importStatus.errors ? 'all' : `first ${importStatus.errorSamples.length}`} error{importStatus.errors === 1 ? '' : 's'}
                          </summary>
                          <ul className="mt-1 ml-4 list-disc text-[11px] text-red-800 space-y-1">
                            {importStatus.errorSamples.map((s, i) => (
                              <li key={`${s.leadgenId}-${i}`}>
                                <span className="font-mono">{s.leadgenId}</span>
                                {s.formName && <span className="text-red-600"> · {s.formName}</span>}
                                <span className="text-red-700"> — {s.reason}</span>
                                {(s.name || s.email || s.phone) && (
                                  <div className="ml-2 text-red-600">
                                    {s.name && <span>name: <span className="font-mono">{s.name}</span></span>}
                                    {s.email && <span>{s.name ? ' · ' : ''}email: <span className="font-mono">{s.email}</span></span>}
                                    {s.phone && <span>{(s.name || s.email) ? ' · ' : ''}phone: <span className="font-mono">{s.phone}</span></span>}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                  {importStatus.status === 'error' && (
                    <div className="text-red-700">
                      Last run failed: {importStatus.errorMessage || 'unknown error'}
                    </div>
                  )}
                </div>
              )}

              {/* Second sub-strip — Messenger / IG DM threads. Same
                  structure as the Lead Ads strip above; renders only
                  when there's status data so the panel stays compact
                  on a fresh connection. */}
              {messengerStatus && (
                <div className="mt-2 pt-3 border-t border-indigo-200 text-xs text-indigo-900">
                  <div className="font-semibold mb-1">Messenger / Instagram DM threads</div>
                  {messengerStatus.status === 'running' && (
                    <div className="flex items-center gap-2">
                      <RefreshCw size={12} className="animate-spin" />
                      <span>
                        Running… {messengerStatus.messagesIngested} message(s) across {messengerStatus.conversationCount} thread(s); {messengerStatus.leadsCreated} new lead(s), {messengerStatus.leadsMatched} matched{messengerStatus.leadsSkippedNoContact ? `, ${messengerStatus.leadsSkippedNoContact} skipped (no contact)` : ''}{messengerStatus.errors ? `, ${messengerStatus.errors} errors` : ''}.
                      </span>
                    </div>
                  )}
                  {messengerStatus.status === 'completed' && (
                    <div>
                      <div>
                        Last run: <strong>{messengerStatus.messagesIngested}</strong> message(s) across {messengerStatus.conversationCount} thread(s); {messengerStatus.leadsCreated} new lead(s), {messengerStatus.leadsMatched} matched{messengerStatus.leadsSkippedNoContact ? `, ${messengerStatus.leadsSkippedNoContact} skipped (no contact)` : ''}{messengerStatus.errors ? `, ${messengerStatus.errors} errors` : ''} — finished {messengerStatus.finishedAt ? new Date(messengerStatus.finishedAt).toLocaleString() : ''}.
                      </div>
                      {messengerStatus.errors > 0 && Array.isArray(messengerStatus.errorSamples) && messengerStatus.errorSamples.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-red-700 hover:text-red-900 select-none">
                            View {messengerStatus.errorSamples.length === messengerStatus.errors ? 'all' : `first ${messengerStatus.errorSamples.length}`} error{messengerStatus.errors === 1 ? '' : 's'}
                          </summary>
                          <ul className="mt-1 ml-4 list-disc text-[11px] text-red-800 space-y-1">
                            {messengerStatus.errorSamples.map((s, i) => (
                              <li key={`${s.conversationId}-${i}`}>
                                <span className="font-mono">{s.conversationId}</span>
                                {s.platform && <span className="text-red-600"> · {s.platform}</span>}
                                <span className="text-red-700"> — {s.reason}</span>
                                {(s.name || s.email || s.phone || s.remoteName) && (
                                  <div className="ml-2 text-red-600">
                                    {s.remoteName && <span>fb: <span className="font-mono">{s.remoteName}</span></span>}
                                    {s.name && <span>{s.remoteName ? ' · ' : ''}name: <span className="font-mono">{s.name}</span></span>}
                                    {s.email && <span>{(s.remoteName || s.name) ? ' · ' : ''}email: <span className="font-mono">{s.email}</span></span>}
                                    {s.phone && <span>{(s.remoteName || s.name || s.email) ? ' · ' : ''}phone: <span className="font-mono">{s.phone}</span></span>}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                  {messengerStatus.status === 'error' && (
                    <div className="text-red-700">
                      Last run failed: {messengerStatus.errorMessage || 'unknown error'}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        )}

        {pickerKey && pickerNeedPageId && (
          <div className="space-y-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm text-indigo-900">
              We couldn't auto-list your pages (this can happen for pages owned by a Business Portfolio). Paste your Facebook Page ID and we'll resolve it.
            </p>
            <input
              value={pickerManualPageId}
              onChange={(e) => setPickerManualPageId(e.target.value)}
              placeholder="e.g. 104285…928"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md font-mono"
            />
            <div className="flex gap-2">
              <button
                onClick={() => submitPagePicker.mutate({ key: pickerKey, pageId: pickerManualPageId })}
                disabled={!pickerManualPageId || submitPagePicker.isLoading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                {submitPagePicker.isLoading ? 'Connecting…' : 'Connect this page'}
              </button>
              <button
                onClick={() => { setPickerKey(null); setPickerManualPageId(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >Cancel</button>
            </div>
          </div>
        )}

        {pickerKey && !pickerNeedPageId && pickerPages.length > 0 && (
          <div className="space-y-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm text-indigo-900">
              You manage multiple Facebook Pages. Pick the one that runs your Lead Ads:
            </p>
            <div className="space-y-1">
              {pickerPages.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 bg-white rounded border border-gray-200 hover:border-primary-300 cursor-pointer">
                  <input
                    type="radio"
                    name="meta-page"
                    value={p.id}
                    checked={pickerSelected === p.id}
                    onChange={(e) => setPickerSelected(e.target.value)}
                  />
                  <span className="text-sm font-medium">{p.name}</span>
                  {p.category && <span className="text-xs text-gray-500">— {p.category}</span>}
                  <code className="ml-auto text-[10px] text-gray-400 font-mono">{p.id}</code>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => submitPagePicker.mutate({ key: pickerKey, pageId: pickerSelected })}
                disabled={!pickerSelected || submitPagePicker.isLoading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                {submitPagePicker.isLoading ? 'Connecting…' : 'Connect'}
              </button>
              <button
                onClick={() => { setPickerKey(null); setPickerPages([]); setPickerSelected(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >Cancel</button>
            </div>
          </div>
        )}
      </Section>

      {/* Meta Conversions API — lead stages back to Meta */}
      <MetaConversionsSection Section={Section} Field={Field} Icon={Send} />

      {/* TikTok Lead Ads */}
      <Section title="TikTok Lead Ads" Icon={Music2} status={data.tiktok?.isConfigured ? 'ok' : 'warn'}>
        {data.tiktok?.appConfigReady === false && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            The platform's TikTok app credentials aren't configured on the server yet. Your administrator needs to set <code>TIKTOK_APP_ID</code>, <code>TIKTOK_APP_SECRET</code> and <code>TIKTOK_REDIRECT_URI</code> before you can connect a TikTok ad account.
          </div>
        )}

        {!data.tiktok?.isConfigured && !tiktokPickerKey && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Connect your TikTok Ads account to ingest TikTok Instant Form (Lead Generation) submissions in real-time. Each lead lands in your Inbox and Leads list automatically — no manual export.
            </p>
            <button
              onClick={() => startTiktokOAuth.mutate()}
              disabled={startTiktokOAuth.isLoading || data.tiktok?.appConfigReady === false}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-black hover:bg-gray-800 disabled:opacity-50 text-white rounded-md text-sm font-semibold shadow-sm"
            >
              <Music2 size={16} />
              {startTiktokOAuth.isLoading ? 'Redirecting…' : 'Connect with TikTok'}
            </button>
            {data.tiktok?.webhookUrl && (
              <Field label="Webhook URL (for TikTok developer portal)" hint="Paste this into your TikTok Marketing API app's webhook configuration.">
                <CopyableUrl url={data.tiktok.webhookUrl} />
              </Field>
            )}
          </div>
        )}

        {/* Multi-advertiser picker — appears when the OAuth callback
            authorised more than one Ads Manager account. Independent
            from Meta / Google pickers; uses the dedicated
            /tiktok/oauth/select-advertiser endpoint. */}
        {tiktokPickerKey && tiktokAdvertisers.length > 0 && (
          <div className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-900">
              You authorised more than one TikTok Ads Manager account. Pick the one that runs your Lead Generation forms:
            </p>
            <div className="space-y-1">
              {tiktokAdvertisers.map((a) => (
                <label key={a.advertiserId} className="flex items-center gap-2 px-3 py-2 bg-white rounded border border-gray-200 hover:border-primary-300 cursor-pointer">
                  <input
                    type="radio"
                    name="tiktok-advertiser"
                    value={a.advertiserId}
                    checked={tiktokSelected === a.advertiserId}
                    onChange={(e) => setTiktokSelected(e.target.value)}
                  />
                  <span className="text-sm font-medium">{a.advertiserName || 'Unnamed advertiser'}</span>
                  <code className="ml-auto text-[10px] text-gray-400 font-mono">{a.advertiserId}</code>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => submitTiktokPicker.mutate({ key: tiktokPickerKey, advertiserId: tiktokSelected })}
                disabled={!tiktokSelected || submitTiktokPicker.isLoading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                {submitTiktokPicker.isLoading ? 'Connecting…' : 'Connect'}
              </button>
              <button
                onClick={() => { setTiktokPickerKey(null); setTiktokAdvertisers([]); setTiktokSelected(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >Cancel</button>
            </div>
          </div>
        )}

        {data.tiktok?.isConfigured && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Link2 size={16} className="text-green-700 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-green-900">Connected to TikTok</div>
                  <div className="text-green-800 mt-0.5">
                    Advertiser: <strong>{data.tiktok.advertiserName || data.tiktok.advertiserId}</strong>
                  </div>
                  <div className="text-green-700 text-xs mt-1">
                    TikTok Lead Ads from this advertiser will land in your Inbox automatically.
                  </div>
                </div>
              </div>
              <button
                onClick={() => disconnectTiktok.mutate()}
                disabled={disconnectTiktok.isLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-700 rounded-md text-xs font-medium"
              >
                <Unlink size={12} />
                {disconnectTiktok.isLoading ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>

            {/* Real-time delivery status. After OAuth, the server
                auto-calls TikTok's /subscription/subscribe/ to register
                this org's advertiser_id → our webhook URL. If that
                failed (missing scope, transient error) the strip
                turns amber and exposes a Re-subscribe button. */}
            {data.tiktok.subscribedAt ? (
              <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-semibold text-green-900">Real-time webhook active</div>
                    <div className="text-xs text-green-800 mt-0.5">
                      TikTok is pushing new Instant Form lead submissions to this CRM automatically. Subscribed {new Date(data.tiktok.subscribedAt).toLocaleString()}.
                    </div>
                  </div>
                  <button
                    onClick={() => resubscribeTiktok.mutate()}
                    disabled={resubscribeTiktok.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-green-100 disabled:opacity-50 border border-green-300 text-green-900 rounded-md text-xs font-medium whitespace-nowrap"
                  >
                    <RefreshCw size={12} />
                    {resubscribeTiktok.isLoading ? 'Re-subscribing…' : 'Re-subscribe'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-semibold text-amber-900">Webhook subscription not active</div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      {data.tiktok.lastSubscribeError
                        ? `Last subscribe attempt failed: ${data.tiktok.lastSubscribeError}`
                        : 'TikTok lead events are not subscribed yet. Click Re-subscribe to register this advertiser with our webhook.'}
                    </div>
                  </div>
                  <button
                    onClick={() => resubscribeTiktok.mutate()}
                    disabled={resubscribeTiktok.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-xs font-medium whitespace-nowrap"
                  >
                    <RefreshCw size={12} />
                    {resubscribeTiktok.isLoading ? 'Subscribing…' : 'Re-subscribe'}
                  </button>
                </div>
              </div>
            )}

            {/* Historical backfill — same UX as Meta. Pages through
                TikTok's Lead Generation list back 90 days and feeds
                each lead through the same captureInbound pipeline the
                webhook uses, so live and historical leads share one
                code path. Idempotent — re-running is safe. */}
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm flex-1">
                  <div className="font-semibold text-indigo-900">Import past TikTok leads</div>
                  <div className="text-indigo-800 text-xs mt-0.5">
                    Pulls every TikTok Lead Generation submission from the past 90 days into the CRM in one shot. Already-imported leads are skipped — safe to re-run. TikTok keeps Lead Generation data for ~90 days, so anything older isn't reachable from this button.
                  </div>
                </div>
                <button
                  onClick={() => importTiktokHistorical.mutate({ requireContact: tiktokRequireContact })}
                  disabled={importTiktokHistorical.isLoading || tiktokImportStatus?.status === 'running'}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-xs font-medium whitespace-nowrap"
                >
                  {tiktokImportStatus?.status === 'running' ? 'Running…'
                    : importTiktokHistorical.isLoading ? 'Starting…'
                    : 'Import now'}
                </button>
              </div>
              <label className="mt-2 flex items-center gap-2 text-[11px] text-indigo-900">
                <input
                  type="checkbox"
                  checked={tiktokRequireContact}
                  onChange={(e) => setTiktokRequireContact(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                />
                Skip leads with no phone and no email
              </label>
              {tiktokImportStatus && (
                <div className="mt-3 pt-3 border-t border-indigo-200 text-xs text-indigo-900">
                  {tiktokImportStatus.status === 'running' && (
                    <div className="flex items-center gap-2">
                      <RefreshCw size={12} className="animate-spin" />
                      <span>
                        Running… {tiktokImportStatus.captured} captured, {tiktokImportStatus.alreadyExisted} already existed{tiktokImportStatus.errors ? `, ${tiktokImportStatus.errors} errors` : ''}{tiktokImportStatus.totalLeads ? ` (of ${tiktokImportStatus.totalLeads})` : ''}.
                      </span>
                    </div>
                  )}
                  {tiktokImportStatus.status === 'completed' && (
                    <div>
                      <div>
                        Last run: <strong>{tiktokImportStatus.captured}</strong> new, {tiktokImportStatus.alreadyExisted} already existed{tiktokImportStatus.skippedNoContact ? `, ${tiktokImportStatus.skippedNoContact} skipped (no contact)` : ''}{tiktokImportStatus.errors ? `, ${tiktokImportStatus.errors} errors` : ''} — finished {tiktokImportStatus.finishedAt ? new Date(tiktokImportStatus.finishedAt).toLocaleString() : ''}.
                      </div>
                      {tiktokImportStatus.errors > 0 && Array.isArray(tiktokImportStatus.errorSamples) && tiktokImportStatus.errorSamples.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-red-700 hover:text-red-900 select-none">
                            View {tiktokImportStatus.errorSamples.length === tiktokImportStatus.errors ? 'all' : `first ${tiktokImportStatus.errorSamples.length}`} error{tiktokImportStatus.errors === 1 ? '' : 's'}
                          </summary>
                          <ul className="mt-1 ml-4 list-disc text-[11px] text-red-800 space-y-1">
                            {tiktokImportStatus.errorSamples.map((s, i) => (
                              <li key={`${s.leadId}-${i}`}>
                                <span className="font-mono">{s.leadId}</span>
                                <span className="text-red-700"> — {s.reason}</span>
                                {(s.name || s.email || s.phone) && (
                                  <div className="ml-2 text-red-600">
                                    {s.name && <span>name: <span className="font-mono">{s.name}</span></span>}
                                    {s.email && <span>{s.name ? ' · ' : ''}email: <span className="font-mono">{s.email}</span></span>}
                                    {s.phone && <span>{(s.name || s.email) ? ' · ' : ''}phone: <span className="font-mono">{s.phone}</span></span>}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                  {tiktokImportStatus.status === 'error' && (
                    <div className="text-red-800">
                      Last run failed: {tiktokImportStatus.errorMessage || 'unknown error'}.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Section>

      {/* Google Ads Lead Form Assets — two paths share the section: V2 OAuth
          (Connect with Google → auto-attach webhook to every lead form) and
          V1 manual (paste URL + key into Google Ads by hand). The OAuth
          path mints the same webhookSecret so the inbound endpoint
          verifies both flows identically. */}
      <Section title="Google Ads Lead Forms" Icon={Target} status={data.googleAds?.isConfigured ? 'ok' : 'warn'}>
        <p className="text-sm text-gray-600 mb-3">
          Capture submissions from Google Ads Lead Form Assets in real-time. Connect with Google and we'll auto-attach the webhook to every lead form on your account — or generate a secret below and paste it into Google Ads by hand.
        </p>

        {/* OAuth env vars not on server → can't show Connect button */}
        {data.googleAds?.clientReady === false && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            The platform's Google Ads OAuth credentials aren't configured on the server yet. Your administrator needs to set <code>GOOGLE_ADS_CLIENT_ID</code>, <code>GOOGLE_ADS_CLIENT_SECRET</code>, <code>GOOGLE_ADS_REDIRECT_URI</code> and <code>GOOGLE_ADS_DEVELOPER_TOKEN</code> before "Connect with Google" works.
          </div>
        )}
        {data.googleAds?.clientReady && data.googleAds?.devTokenReady === false && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            Google Ads OAuth is enabled but the developer token isn't approved yet — Google reviews these in 1–2 weeks. You can connect now (the picker works) but auto-attach to lead forms will be skipped until the token is set.
          </div>
        )}

        {/* OAuth picker: rendered after Google redirects back. Modal-style
            inline panel so the operator can pick a customer without leaving
            the page. */}
        {googlePickerKey && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm font-semibold text-blue-900 mb-2">Pick the Google Ads account to connect</div>
            {googleNeedDevToken && (
              <p className="text-xs text-amber-800 mb-3">
                Developer token not configured yet. We'll save your OAuth tokens; auto-attach will run once the token is added.
              </p>
            )}
            {googleNameLookupError && !googleNeedDevToken && (
              <p className="text-xs text-amber-800 mb-3">
                Account names can't be loaded yet — your Google Ads developer token is in Test Access. Pick by ID for now (Manager badge marks the MCC); names auto-fill once Google approves Basic Access.
              </p>
            )}
            {googleCustomers.length === 0 ? (
              <p className="text-xs text-gray-600 mb-3">
                No Google Ads accounts visible to this Google login. Make sure you signed in with the right account.
              </p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
                {googleCustomers.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer">
                    <input
                      type="radio"
                      name="google-customer"
                      value={c.id}
                      checked={googleSelectedCustomer === c.id}
                      onChange={() => setGoogleSelectedCustomer(c.id)}
                    />
                    <span className="text-sm">
                      <span className="font-medium">{c.name || `Customer ${c.id}`}</span>
                      <span className="ml-2 font-mono text-xs text-gray-500">{c.id}</span>
                      {c.isManager && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Manager</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => selectGoogleCustomer.mutate({ key: googlePickerKey, customerId: googleSelectedCustomer })}
                disabled={!googleSelectedCustomer || selectGoogleCustomer.isLoading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                {selectGoogleCustomer.isLoading ? 'Connecting…' : 'Connect & attach webhook'}
              </button>
              <button
                onClick={() => { setGooglePickerKey(null); setGoogleCustomers([]); }}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!data.googleAds?.oauthConnected && !data.googleAds?.isConfigured ? (
          <div className="space-y-3">
            <button
              onClick={() => startGoogleOAuth.mutate()}
              disabled={startGoogleOAuth.isLoading || !data.googleAds?.clientReady}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4285F4] hover:bg-[#3B78E0] disabled:opacity-50 text-white rounded-md text-sm font-semibold shadow-sm"
            >
              <Target size={16} />
              {startGoogleOAuth.isLoading ? 'Redirecting…' : 'Connect with Google'}
            </button>
            <div className="text-xs text-gray-500">
              Or set up manually:
            </div>
            <button
              onClick={() => regenerateGoogleSecret.mutate()}
              disabled={regenerateGoogleSecret.isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 rounded-md text-sm font-medium"
            >
              {regenerateGoogleSecret.isLoading ? 'Generating…' : 'Generate webhook secret manually'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Connected via OAuth — show customer name + reattach button */}
            {data.googleAds?.oauthConnected && (
              <div className="flex items-start justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Link2 size={16} className="text-blue-700 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-blue-900">
                      Connected via Google: {data.googleAds.customerName || `Customer ${data.googleAds.customerId}`}
                    </div>
                    <div className="text-blue-800 text-xs mt-0.5 font-mono">{data.googleAds.customerId}</div>
                    <div className="text-blue-700 text-xs mt-1">
                      {data.googleAds.lastAttachAt
                        ? (data.googleAds.lastAttachError
                            ? <span className="text-amber-800">⚠ Auto-attach blocked: {data.googleAds.lastAttachError}. Use the manual webhook URL + key below until resolved.</span>
                            : data.googleAds.lastAttachTotal === 0
                              ? <span>No Lead Form Assets found on this customer. If this is a Manager (MCC) account, lead forms live on the child advertiser — disconnect and reconnect, picking the child id.</span>
                              : <span>Webhook attached to {data.googleAds.lastAttachSucceeded} of {data.googleAds.lastAttachTotal} lead form(s). Re-run "Attach webhooks" after creating new forms.</span>
                          )
                        : 'Auto-attach not run yet. Click "Attach webhooks" to push the webhook config to every form on this customer.'}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => attachGoogleWebhooks.mutate()}
                    disabled={attachGoogleWebhooks.isLoading || !data.googleAds.devTokenReady}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium"
                  >
                    <RefreshCw size={12} />
                    {attachGoogleWebhooks.isLoading ? 'Attaching…' : 'Attach webhooks'}
                  </button>
                  <button
                    onClick={() => disconnectGoogleOAuth.mutate()}
                    disabled={disconnectGoogleOAuth.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-700 rounded-md text-xs font-medium"
                  >
                    <Unlink size={12} />
                    {disconnectGoogleOAuth.isLoading ? '…' : 'Unlink Google'}
                  </button>
                </div>
              </div>
            )}

            {/* 4-state health indicator — operators need to know AT A GLANCE
                whether Google can actually reach the webhook, not just that
                we minted a secret. States ranked by signal strength:
                  LIVE       — real lead in the last 7 days
                  CONFIRMED  — Google "Send test data" or any hit recently
                  IDLE       — connectivity confirmed once but quiet now
                  PENDING    — secret minted but Google hasn't hit us yet  */}
            {(() => {
              const lastLead = data.googleAds.lastReceivedAt ? new Date(data.googleAds.lastReceivedAt) : null;
              const lastTest = data.googleAds.lastTestReceivedAt ? new Date(data.googleAds.lastTestReceivedAt) : null;
              const fmt = (d) => {
                const diffMs = Date.now() - d.getTime();
                const m = Math.floor(diffMs / 60000), h = Math.floor(diffMs / 3600000), days = Math.floor(diffMs / 86400000);
                if (m < 1) return 'just now';
                if (m < 60) return `${m}m ago`;
                if (h < 24) return `${h}h ago`;
                return `${days}d ago`;
              };
              const sevenDaysAgo = Date.now() - 7 * 86400_000;
              let state, color, title, body;
              if (lastLead && lastLead.getTime() > sevenDaysAgo) {
                state = 'LIVE'; color = 'green';
                title = 'Live — real leads flowing';
                body = `Last lead received ${fmt(lastLead)} (${lastLead.toLocaleString()})`;
              } else if (lastLead || (lastTest && lastTest.getTime() > sevenDaysAgo)) {
                state = 'CONFIRMED'; color = 'blue';
                title = 'Connected and verified — waiting for real leads';
                body = lastTest
                  ? `Google reached us ${fmt(lastTest)} via "Send test data". Path is open; real submissions will arrive once your form goes live.`
                  : `Last lead ${fmt(lastLead)} — quiet but path verified.`;
              } else if (lastTest) {
                state = 'IDLE'; color = 'amber';
                title = 'Verified historically, but quiet';
                body = `Last contact from Google ${fmt(lastTest)}. If your forms should be active, send another test or check the Webhook URL/Key in Google Ads.`;
              } else {
                state = 'PENDING'; color = 'amber';
                title = 'No webhook hits yet';
                body = 'Google has never reached this URL. Either no form is configured with the URL+key below, or Google rejected the URL. Use "Send test data" inside Google Ads → Lead form, or our "Send test lead" button to verify.';
              }
              const colorMap = {
                green: 'bg-green-50 border-green-200 text-green-900 text-green-700 text-green-800',
                blue:  'bg-blue-50 border-blue-200 text-blue-900 text-blue-700 text-blue-800',
                amber: 'bg-amber-50 border-amber-200 text-amber-900 text-amber-700 text-amber-800',
              };
              const [bg, border, titleC, bodyC, dotC] = colorMap[color].split(' ');
              return (
                <div className={`flex items-start justify-between gap-3 p-3 ${bg} border ${border} rounded-lg`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-1 w-2.5 h-2.5 rounded-full ${dotC.replace('text-', 'bg-')} ${color === 'green' ? 'animate-pulse' : ''}`} />
                    <div className="text-sm">
                      <div className={`font-semibold ${titleC}`}>{title}</div>
                      <div className={`${bodyC} text-xs mt-0.5`}>{body}</div>
                      <div className="text-[10px] text-gray-500 mt-1 font-mono">state: {state}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => disconnectGoogle.mutate()}
                    disabled={disconnectGoogle.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-700 rounded-md text-xs font-medium"
                  >
                    <Unlink size={12} />
                    {disconnectGoogle.isLoading ? 'Disconnecting…' : 'Reset secret'}
                  </button>
                </div>
              );
            })()}

            <Field label="Webhook URL" hint="Paste into Google Ads → Lead form asset → Lead delivery → Webhook URL.">
              <CopyableUrl url={data.googleAds.webhookUrl} />
            </Field>

            <Field
              label="Key (shared secret)"
              hint="Paste into the matching 'Key' field in Google Ads. Anyone with this value can post fake leads — keep it private and rotate if you suspect a leak."
            >
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-gray-50 p-2 border border-gray-200 rounded break-all">
                  {showGoogleSecret ? data.googleAds.webhookSecret : '•'.repeat(Math.min(40, data.googleAds.webhookSecret?.length || 0))}
                </code>
                <button
                  type="button"
                  onClick={() => setShowGoogleSecret((v) => !v)}
                  className="p-2 text-gray-500 hover:text-primary-600"
                  title={showGoogleSecret ? 'Hide' : 'Show'}
                >
                  {showGoogleSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(data.googleAds.webhookSecret); toast.success('Copied'); }}
                  className="p-2 text-gray-500 hover:text-primary-600"
                  title="Copy"
                >
                  <Copy size={14} />
                </button>
              </div>
            </Field>

            <div className="flex gap-2">
              <button
                onClick={() => sendGoogleTestLead.mutate()}
                disabled={sendGoogleTestLead.isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-xs font-medium"
              >
                <Play size={12} />
                {sendGoogleTestLead.isLoading ? 'Sending…' : 'Send test lead'}
              </button>
              <button
                onClick={() => regenerateGoogleSecret.mutate()}
                disabled={regenerateGoogleSecret.isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 rounded-md text-xs font-medium"
              >
                <RefreshCw size={12} />
                {regenerateGoogleSecret.isLoading ? 'Rotating…' : 'Rotate secret'}
              </button>
            </div>
          </div>
        )}
      </Section>

      <Section title="WhatsApp" Icon={MessageCircle} status={data.whatsapp?.isConfigured ? 'ok' : 'warn'}>
        <p className="text-sm text-gray-700 mb-3">
          Two-way WhatsApp in the Inbox via the official WhatsApp Business Platform. Your business
          number stays active on the WhatsApp Business phone app — replies typed on the phone
          appear in the timeline too. Connect in one click with the Facebook account that manages
          your business.
        </p>

        {data.whatsapp?.isConfigured && (
          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm mb-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-green-800 font-medium">
                  Connected: {data.whatsapp.displayPhone || data.whatsapp.phoneNumberId}
                  {data.whatsapp.verifiedName ? ` — ${data.whatsapp.verifiedName}` : ''}
                </div>
                <div className="text-green-700 text-xs mt-0.5">
                  {data.whatsapp.connectedVia === 'embedded_signup' ? 'Connected with WhatsApp (one-click signup)' : 'Connected with pasted credentials'}
                  {data.whatsapp.lastReceivedAt ? ` · Last message received ${new Date(data.whatsapp.lastReceivedAt).toLocaleString()}` : ''}
                </div>
              </div>
              <button
                onClick={() => disconnectWhatsapp.mutate()}
                disabled={disconnectWhatsapp.isLoading}
                className="inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 text-xs font-medium rounded-lg px-3 py-1.5"
              >
                <Unlink size={12} /> Disconnect
              </button>
            </div>
          </div>
        )}

        {data.whatsapp?.isConfigured && data.whatsapp.registrationError && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 mb-4">
            <div className="font-medium">Number registration needs attention</div>
            <div className="text-xs mt-1">{data.whatsapp.registrationError}</div>
            <div className="text-xs mt-1">Incoming messages still arrive; sending from the Inbox may fail until this is resolved. Reconnect after fixing.</div>
          </div>
        )}

        {!data.whatsapp?.isConfigured && data.whatsapp?.pendingPhoneNumbers?.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm mb-4">
            <div className="text-blue-900 font-medium mb-2">Which number should the CRM use?</div>
            <div className="flex flex-col gap-2">
              {data.whatsapp.pendingPhoneNumbers.map((n) => (
                <button
                  key={n.phoneNumberId}
                  onClick={() => selectWhatsappPhone.mutate(n.phoneNumberId)}
                  disabled={selectWhatsappPhone.isLoading}
                  className="flex flex-wrap items-center justify-between gap-2 text-left bg-white border border-blue-200 hover:border-blue-400 rounded-lg px-3 py-2"
                >
                  <span className="font-medium text-gray-900">{n.displayPhone || n.phoneNumberId}</span>
                  <span className="text-xs text-gray-500">{n.verifiedName}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!data.whatsapp?.isConfigured && (
          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => startWhatsappSignup('coexistence')}
                disabled={waSignupBusy || !data.whatsapp?.embeddedSignupReady}
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                <MessageCircle size={16} />
                {waSignupBusy ? 'Waiting for Meta…' : 'Connect with WhatsApp'}
              </button>
              <button
                onClick={() => startWhatsappSignup('cloud')}
                disabled={waSignupBusy || !data.whatsapp?.embeddedSignupReady}
                className="text-sm text-gray-600 hover:text-gray-900 underline disabled:opacity-50"
              >
                Connect a new number instead (Cloud API only)
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Log in with the Facebook account that manages your business. Keep using WhatsApp Business on your
              phone — you'll scan a QR code from the app and messages sync both ways.
            </p>
            {!data.whatsapp?.embeddedSignupReady && (
              <p className="text-xs text-amber-700 mt-2">
                One-click signup is not enabled on this server yet. Ask your platform administrator to finish the WhatsApp setup.
              </p>
            )}
          </div>
        )}


        {!data.whatsapp?.appConfigReady && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            Platform Meta app config (META_APP_ID / META_APP_SECRET / META_VERIFY_TOKEN) is incomplete — webhook signature verification and the handshake need it.
          </div>
        )}
      </Section>

      {/* IMAP — diagnostics-only. Configuration moved to SMTP Settings
          (single email account, single set of credentials). This panel
          just shows status, last sync, and a one-shot 'poll now' button
          for testing without waiting for the next 5-minute tick. */}
      <Section title="Email inbox (IMAP)" Icon={Mail} status={data.imap.isConfigured ? 'ok' : 'warn'}>
        <p className="text-sm text-gray-700 mb-3">
          IMAP credentials are reused from your <a href="/smtp-settings" className="text-primary-600 hover:underline">SMTP Settings</a> — same email account, same password. Set the IMAP host there.
        </p>
        {!data.imap.isConfigured ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
            IMAP not configured yet. Go to <a href="/smtp-settings" className="underline font-medium">SMTP Settings</a> → "Inbound Email (IMAP)" and enter the IMAP host.
          </div>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-900 mb-3">
              <div className="font-semibold">IMAP active</div>
              <div className="text-xs mt-1">
                Polls automatically every 5 min. {data.imap.autoCreateLeads ? 'Auto-creates leads from new senders.' : 'Only emails from existing leads are captured.'}
              </div>
              {data.imap.lastSyncAt && (
                <div className="text-xs mt-1 text-green-700">Last poll: {new Date(data.imap.lastSyncAt).toLocaleString()}</div>
              )}
            </div>
            <button
              onClick={() => testImap.mutate()}
              disabled={testImap.isLoading}
              className="inline-flex items-center gap-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 rounded-md text-sm"
            >
              <Play size={12} /> {testImap.isLoading ? 'Polling...' : 'Poll now'}
            </button>
          </>
        )}
      </Section>

      {/* Recent webhook activity — last 50 inbound hits, refreshes every 10s */}
      <Section title="Recent webhook activity" Icon={Activity}>
        <div className="flex items-center justify-between -mt-2 mb-3">
          <p className="text-xs text-gray-500">
            Last 50 inbound webhook hits (auto-refreshes every 10 s, kept for 7 days).
          </p>
          <button
            type="button"
            onClick={() => refetchEvents()}
            disabled={fetchingEvents}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 text-gray-600 hover:text-primary-600 disabled:opacity-50"
          >
            <RefreshCw size={12} className={fetchingEvents ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        {events?.events?.length > 0 ? (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">When</th>
                  <th className="px-4 py-2 text-left font-medium">Channel</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Outcome</th>
                  <th className="px-4 py-2 text-left font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.events.map((ev) => (
                  <tr key={ev._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                      {new Date(ev.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap font-mono text-gray-700">{channelLabel(ev.channel)}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-mono">{ev.status}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${outcomePill(ev.outcome)}`}>
                        {ev.outcome}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      <div className="font-medium">{ev.summary || '—'}</div>
                      {ev.error && <div className="text-red-600 mt-0.5">{ev.error}</div>}
                      {ev.payloadPreview && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[10px] text-gray-400 hover:text-gray-600">payload</summary>
                          <pre className="mt-1 text-[10px] bg-gray-50 p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">{ev.payloadPreview}</pre>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-sm text-gray-500">
            No webhook activity yet. Trigger Meta's <a href="https://developers.facebook.com/tools/lead-ads-testing" target="_blank" rel="noreferrer" className="text-primary-600 underline">Lead Ads Testing Tool</a> or send a test SMS to populate this list.
          </div>
        )}
      </Section>
    </div>
  );
};

export default InboundSettings;
